import { useState, useEffect } from "react";
import api from "@/services/api";

export interface BankAccount {
  accountNumber:    string;
  /** Nome amigável definido no BI (filtro SEMOG) */
  displayName?:     string;
  amount:           number;
  reconciledAmount: number;
  companyId:        number | null;
  accountStatus:    string;
  balanceDate:      string;
}

export interface BankBalanceResult {
  total:       number;
  balanceDate: string;
  accounts:    BankAccount[];
  loading:     boolean;
  error:       string | null;
}

interface ApiResponse {
  success:     boolean;
  total:       number;
  balanceDate: string;
  accounts:    BankAccount[];
}

/**
 * Busca o saldo bancário consolidado via GET /api/bank-balance.
 * O backend tenta a data de hoje e recua até encontrar valor ≠ 0.
 * Só dispara a requisição quando `enabled` (perfil Administrador).
 */
export function useBankBalance(enabled = true): BankBalanceResult {
  const [total,       setTotal]       = useState(0);
  const [balanceDate, setBalanceDate] = useState("");
  const [accounts,    setAccounts]    = useState<BankAccount[]>([]);
  const [loading,     setLoading]     = useState(enabled);
  const [error,       setError]       = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setTotal(0);
      setBalanceDate("");
      setAccounts([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get<ApiResponse>("/api/bank-balance")
      .then(({ data }) => {
        if (cancelled) return;
        if (data.success) {
          setTotal(data.total);
          setBalanceDate(data.balanceDate);
          setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
        } else {
          setError("Erro ao buscar saldo bancário.");
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || "Erro ao buscar saldo bancário.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [enabled]);

  return { total, balanceDate, accounts, loading, error };
}
